import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { Link } from 'react-router-dom';
import { Container } from 'reactstrap';

import InlineMetaCriticLogo from 'Components/HallOfFame/InlineMetaCriticLogo';
import Meta from 'Components/Utility/SEO/Meta';
import ProductListCard from 'Components/Cards/ProductListCard';
import TopSellersCard from 'Components/TopSellers/TopSellersCard';
import useViewport from 'Hooks/useViewport';


import 'Components/HallOfFame/HallOfFame.scss';

const years = [2022, 2023, 2024, 2025];
const latestYear = years[years.length - 1];

const messages = defineMessages({
  meta_title: {
    id: 'halloffame.meta_title',
    defaultMessage: 'Best Games of {year} | Fanatical',
  },
  meta_description: {
    id: 'halloffame.meta_description',
    defaultMessage: 'The best game releases in {year} rated by Metacritic, featuring numerous Game of the Year winners, new release games, and even previously-exclusive titles new to PC! Feast your eyes on the best of {year}.',
  },
  top_reviewed: {
    id: 'halloffame.top_reviewed',
    defaultMessage: 'Top Reviewed',
  },
  best_discount: {
    id: 'halloffame.best_discount',
    defaultMessage: 'Best Discount',
  },
  lowest_price: {
    id: 'halloffame.lowest_price',
    defaultMessage: 'Lowest Price',
  },
});

const HallOfFameLandingPage = ({
  hits,
  sort,
  setSort,
  siteTheme,
  bestOfYear,
}) => {
  const { formatMessage } = useIntl();
  const { isBelowLg, isAboveMd } = useViewport();

  const options = [
    { name: formatMessage(messages.top_reviewed), type: 'top-reviewed' },
    { name: formatMessage(messages.best_discount), type: 'best-discount' },
    { name: formatMessage(messages.lowest_price), type: 'lowest-price' },
  ];

  return (
    <div className="HallOfFame">
      <Meta
        title={formatMessage(messages.meta_title, { year: bestOfYear })}
        url={`best-games-of-${bestOfYear}`}
        hits={hits}
        description={formatMessage(messages.meta_description, { year: bestOfYear })}
        pageType="CollectionPage"
      />
      <Container>
        <div className="HallOfFame__topSection">
          <div className="HallOfFame__titleSection pt-5">
            <div className="HallOfFame__title">
              <h1>
                <FormattedMessage
                  id="halloffame.title"
                  defaultMessage="Best Games of {bestOfYear}"
                  values={{
                    bestOfYear: <span>{bestOfYear}<React.Fragment>{bestOfYear === latestYear ? <FormattedMessage id="HallOfFame.sofar" defaultMessage=" So Far" /> : <React.Fragment />}</React.Fragment></span>,
                  }}
                />
              </h1>
            </div>
            <InlineMetaCriticLogo siteTheme={siteTheme} />
          </div>

          <p className="seo-blurb-text">
            {bestOfYear === latestYear ?
              <FormattedMessage
                id="halloffame.seoblurb-latest"
                defaultMessage="Discover the Hottest Games of {year}! Immerse yourself in a thrilling year packed with sensational PC game titles including Game of the Year contenders, Metacritic top rated, new release games, and highly anticipated exclusives making their debut on PC. Prepare to be captivated by the unrivalled offerings of {year}"
                values={{ year: bestOfYear }}
              />
              :
              <FormattedMessage
                id="halloffame.seoblurb"
                defaultMessage="The Best Games of {year}! We’ve had a wonderful year full of fantastic PC game titles. On this page, you’ll find all of the best releases throughout the year rated by Metacritic, featuring numerous Game of the Year winners (including the Game of the Year award), new release games, and even previously-exclusive titles new to PC! Feast your eyes on the best of {year}."
                values={{ year: bestOfYear }}
              />
            }
          </p>

          <div className="HallOfFame__filterSortContainer">
            <div className="bundle-pill-filters-container d-none d-lg-flex">
              {years.map(year => <Link to={`/best-games-of-${year}`} className={`pill-filter-container ${bestOfYear === year ? 'selected' : ''}`}>
                {year}
              </Link>,
              )}
            </div>

            <div className="HallOfFame__dropdownSection">
              <div className="HallOfFame__sortDropdownContainer dropdown-container">
                <span className="HallOfFame__sortDropdown__label">
                  <FormattedMessage id="halloffame.sortby" defaultMessage="Sort by" />:
                </span>
                <select value={sort} onChange={e => setSort(e.target.value)}>
                  {options.map(option =>
                    <option
                      key={option.type}
                      value={option.type}
                    >
                      {option.name}
                    </option>,
                  )}
                </select>
              </div>
            </div>
          </div>

        </div>


        {isAboveMd &&
          <ol className="reset-list mb-5">
            {!isEmpty(hits) && hits.map((hit, index) =>
              <li>
                <TopSellersCard
                  showCriticScore
                  showReleaseDate
                  key={`${hit.objectID} ${Math.random()}`}
                  hit={hit}
                  index={index}
                />
              </li>,
            )}
          </ol>
        }

        {isBelowLg &&
          <ol className="product-list-card-list mb-5">
            {hits && hits.map((hit, i) =>
              <ProductListCard
                showReleaseDate
                isMetacritic
                hit={hit}
                index={i}
                key={hit.objectID}
                listCardNameTag="h3"
                listCardContainerTag="li"
              />,
            )}
          </ol>
        }

      </Container>
    </div>
  );
};

HallOfFameLandingPage.propTypes = {
  hits: PropTypes.arrayOf(PropTypes.object),
  sort: PropTypes.string,
  setSort: PropTypes.func.isRequired,
  siteTheme: PropTypes.string,
  bestOfYear: PropTypes.number.isRequired,
};

HallOfFameLandingPage.defaultProps = {
  hits: null,
  sort: '',
  siteTheme: 'dark',
};

export default HallOfFameLandingPage;
